import { zodResolver } from '@hookform/resolvers/zod'
import { useAtomValue } from 'jotai'
import React from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useAsyncFn } from 'react-use'
import * as R from 'remeda'
import { type Asyncify } from 'type-fest'

import {
  EditFilterDialog,
  type EditFilterDialogProps,
} from '~/components/features/EditFilterDialog'
import { SurveyGroupFilterGroupsInput } from '~/components/features/SurveyGroupFilterGroupsInput'
import { graphql } from '~/gql'
import { useGraphQLRequest } from '~/hooks/useGraphQLRequest'
import { useTabulate } from '~/hooks/useTabulate'
import {
  exists,
  mapItemsToObj,
  toSurveyGroupFilterItemInputGroups,
} from '~/utils'

import {
  isVisibleEditDialogAtom,
  onChangeAtom,
  surveyGroupIdAtom,
} from '../../atoms'
import {
  useMainQueryBrands,
  useMainQueryCustomerAttributeGroups,
  useMainQueryQuestions,
} from '../../hooks/useMainQuery'
import {
  useInvalidateSurveyGroupFiltersQuery,
  useSurveyGroupFiltersQueryData,
} from '../../hooks/useSurveyGroupFiltersQuery'

import { type InputValues, schema } from './utils'

const countSurveyGroupTotalAnswersMutationDocument = graphql(`
  mutation SurveyGroupFilterInformation_CountSurveyGroupTotalAnswersMutation(
    $surveyGroupId: String!
    $filter: [[SurveyGroupCustomerAttributeFilterItemInput!]!]!
  ) {
    count: countSurveyGroupTotalAnswers(
      surveyGroupId: $surveyGroupId
      customFilter: { filter: $filter }
    ) {
      awaitId
    }
  }
`)

const countSurveyGroupTotalAnswersQueryDocument = graphql(`
  query SurveyGroupFilterInformation_CountSurveyGroupTotalAnswers(
    $awaitId: String!
  ) {
    count: countSurveyGroupTotalAnswers(awaitId: $awaitId) {
      arrived
      result {
        count
        surveyId
      }
    }
  }
`)

const getSurveyGroupFilterDocument = graphql(`
  query SurveyGroupFilterInformation_GetSurveyGroupFilter(
    $surveyGroupFilterId: ID!
  ) {
    getSurveyGroupFilter(surveyGroupFilterId: $surveyGroupFilterId) {
      id
      customFilter {
        filter {
          category
          question {
            surveyGroupQuestionId
            operation
            answerOptions {
              option
              text
            }
          }
          customerAttribute {
            surveyGroupCustomerAttributeGroupId
            operation
            customerAttributes {
              surveyGroupCustomerAttributeId
              name
            }
          }
          brandSegments {
            surveyGroupBrandId
            segmentNumbers
          }
        }
      }
    }
  }
`)

const createSurveyGroupFilterDocument = graphql(`
  mutation SurveyGroupFilterInformation_CreateSurveyGroupFilter(
    $surveyGroupId: ID!
    $title: String!
    $filter: [[SurveyGroupCustomerAttributeFilterItemInput!]!]!
  ) {
    createSurveyGroupFilter(
      surveyGroupId: $surveyGroupId
      title: $title
      customFilter: { filter: $filter }
    ) {
      id
    }
  }
`)

const deleteSurveyGroupFilterDocument = graphql(`
  mutation SurveyGroupFilterInformation_DeleteSurveyGroupFilter(
    $surveyGroupFilterId: ID!
  ) {
    deleteSurveyGroupFilter(surveyGroupFilterId: $surveyGroupFilterId) {
      result
    }
  }
`)

type FilterItem = InputValues['filterGroups'][number][number]

const resolver = zodResolver(schema)

export type EditSurveyGroupFilterDialogProps = {
  filterGroups: InputValues['filterGroups']
}

export const EditSurveyGroupFilterDialog = ({
  filterGroups,
}: EditSurveyGroupFilterDialogProps) => {
  const { t } = useTranslation()
  const methods = useForm<InputValues>({
    defaultValues: { filterGroups },
    resolver,
  })
  const { control } = methods

  const onChange = useAtomValue(onChangeAtom)
  const [_countSurveyGroupTotalAnswersState, countSurveyGroupTotalAnswers] =
    useTabulate(
      countSurveyGroupTotalAnswersMutationDocument,
      countSurveyGroupTotalAnswersQueryDocument,
    )
  const surveyGroupId = useAtomValue(surveyGroupIdAtom)
  const [handleExecuteState, handleExecute] = useAsyncFn<
    Asyncify<EH<EditFilterDialogProps<FilterItem>, 'onExecute'>>
  >(
    async (filterGroups) => {
      if (!exists(filterGroups)) {
        onChange(undefined)

        return
      }

      const result = await countSurveyGroupTotalAnswers({
        surveyGroupId,
        filter: toSurveyGroupFilterItemInputGroups(filterGroups),
      })

      onChange({
        countMap: mapItemsToObj(result ?? [], R.prop('count'), 'surveyId'),
        filterGroups,
      })
    },
    [countSurveyGroupTotalAnswers, onChange, surveyGroupId],
  )

  const brands = useMainQueryBrands()

  const customerAttributeGroups = useMainQueryCustomerAttributeGroups()
  const validCustomerAttributeGroups = React.useMemo(
    () =>
      customerAttributeGroups.filter(
        ({ customerAttributes }) => 0 < customerAttributes.length,
      ),
    [customerAttributeGroups],
  )

  const questions = useMainQueryQuestions()
  const validQuestions = React.useMemo(
    () => questions.filter(({ answerOptions }) => 0 < answerOptions.length),
    [questions],
  )

  const surveyGroupFilters = useSurveyGroupFiltersQueryData()

  const graphQLRequest = useGraphQLRequest()
  const invalidateSurveyGroupFiltersQuery =
    useInvalidateSurveyGroupFiltersQuery()
  const handleDelete = React.useCallback<
    EH<EditFilterDialogProps<FilterItem>, 'onDelete'>
  >(
    async (surveyGroupFilterId) => {
      await graphQLRequest({
        document: deleteSurveyGroupFilterDocument,
        variables: { surveyGroupFilterId },
      })

      await invalidateSurveyGroupFiltersQuery()
    },

    [graphQLRequest, invalidateSurveyGroupFiltersQuery],
  )

  const handleLoad = React.useCallback<
    EH<EditFilterDialogProps<FilterItem>, 'onLoad'>
  >(
    async (surveyGroupFilterId) => {
      const result = await graphQLRequest({
        document: getSurveyGroupFilterDocument,
        variables: { surveyGroupFilterId },
      })
      const filterGroups = result.getSurveyGroupFilter?.customFilter?.filter

      return filterGroups as InputValues['filterGroups']
    },
    [graphQLRequest],
  )

  const handleSave = React.useCallback<
    EH<EditFilterDialogProps<FilterItem>, 'onSave'>
  >(
    async (title, filterGroups) => {
      const filter = toSurveyGroupFilterItemInputGroups(filterGroups)

      await graphQLRequest({
        document: createSurveyGroupFilterDocument,
        variables: { surveyGroupId, title, filter },
      })

      await invalidateSurveyGroupFiltersQuery()
    },
    [graphQLRequest, invalidateSurveyGroupFiltersQuery, surveyGroupId],
  )

  return (
    <EditFilterDialog
      executing={handleExecuteState.loading}
      filterGroupsInput={
        <SurveyGroupFilterGroupsInput
          brands={brands}
          control={control}
          customerAttributeGroups={validCustomerAttributeGroups}
          questions={validQuestions}
        />
      }
      filters={surveyGroupFilters}
      isVisibleEditDialogAtom={isVisibleEditDialogAtom}
      methods={methods}
      title={t('filter_time_series_data')}
      onDelete={handleDelete}
      onExecute={handleExecute}
      onLoad={handleLoad}
      onSave={handleSave}
    />
  )
}
